import React, { FC } from 'react';
import { Box, Button, Container, Typography } from '@maas-components/core';

export interface PageErrorProps {
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    title?: string;
    message?: string;
    button?: React.ReactElement | undefined;
    buttonText?: string;
    buttonUrl?: string;
    errorType:
        | 'NotFound'
        | 'InternalError'
        | 'AccountsError'
        | 'Unavailable'
        | 'Forbidden'
        | 'ConnectionError'
        | 'Custom';
    image?: React.ReactElement;
    imageUrl?: string;
}

const PageError: FC<PageErrorProps> = ({
    maxWidth = 'md',
    title,
    message,
    button,
    buttonText,
    buttonUrl,
    errorType,
    image,
    imageUrl,
}) => {
    const baseUrl = imageUrl || `https://seller.magalu.com/images/error-page/${errorType}.svg`;

    const types = {
        Custom: {
            title,
            message,
            button,
            buttonText,
            buttonUrl,
            image,
            imageUrl,
        },
        NotFound: {
            title: title || 'Esta página não existe',
            message: message || 'Verifique se o endereço da página foi digitado corretamente.',
            button,
            buttonText,
            buttonUrl,
            image,
            imageUrl: baseUrl,
        },
        InternalError: {
            title: title || 'Erro no servidor interno',
            message:
                message ||
                'Algo deu errado, mas já estamos tentando resolver para regularizar o quanto antes. Por favor, tente novamente mais tarde.',
            button,
            buttonText,
            buttonUrl,
            image,
            imageUrl: baseUrl,
        },
        AccountsError: {
            title: title || 'Problemas na conta',
            message:
                message ||
                'Estamos tendo algum problema com a sua conta, mas não se preocupe! Entre em contato com nosso atendimento para resolvermos.',
            button,
            buttonText: buttonText || 'Acessar atendimento ao seller',
            buttonUrl: buttonUrl || '/suporte',
            image,
            imageUrl: baseUrl,
        },
        Unavailable: {
            title: title || 'Página indisponível temporariamente',
            message:
                message ||
                'Está página está em manutenção e voltará dentro de 2 horas. Por favor, tente novamente mais tarde.',
            button,
            buttonText,
            buttonUrl,
            image,
            imageUrl: baseUrl,
        },
        Forbidden: {
            title: title || 'Acesso restrito',
            message:
                message ||
                'Você precisa de permissão para acessar esta página. Entre em contato com a área responsável para obter informações.',
            button: button || '',
            buttonText: buttonText || 'Acessar atendimento ao seller',
            buttonUrl: buttonUrl || '/suporte',
            image,
            imageUrl: baseUrl,
        },
        ConnectionError: {
            title: title || 'Erro de conexão/internet',
            message: message || 'Tente reiniciar sua internet e atualizar esta página(?)',
            button,
            buttonText,
            buttonUrl,
            image,
            imageUrl: baseUrl,
        },
    };

    const selectedType = types[errorType];

    return (
        <Container maxWidth={maxWidth}>
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Box mt={20}>
                    <Typography variant="h2">{selectedType.title}</Typography>
                </Box>
                <Box mt={2}>
                    <Typography variant="body1" color="textSecondary">
                        {selectedType.message}
                    </Typography>
                </Box>
                {selectedType.button ? (
                    selectedType.button
                ) : (
                    <Box mt={4} textAlign="center">
                        {selectedType.buttonText ? (
                            <Button href={selectedType.buttonUrl} variant="contained" color="primary">
                                {selectedType.buttonText}
                            </Button>
                        ) : null}
                    </Box>
                )}
                {selectedType.image && <Box mt={4}>{selectedType.image}</Box>}
                {selectedType.imageUrl && (
                    <Box mt={4}>
                        <img alt={title} src={selectedType.imageUrl} width="100%" />
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default PageError;
